import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Button,
  List,
  Avatar,
  Select,
  Divider,
  Spin,
  Typography,
  Row,
  Col
} from "antd";
import { UserOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { Error, Success } from "../Messages/Messages";
import UserSearchComponent from "../UsersSearch";

import { useTranslation } from "react-i18next";
const { Text } = Typography;

const ShareModal = ({ visible, onCancel, resource, type = "document", apiUrl, updateParent }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({ owner: null, sharedWith: [] });
  const [submitting, setSubmitting] = useState(false);

  const fetchPermissions = async () => {
    if (!resource?._id) return;
    setLoading(true);
    try {
      const endpoint = type === "folder" ? "folders" : "documents";
      const response = await axios.get(`${apiUrl}/${endpoint}/permissions/${resource._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setPermissions(response.data.data);
    } catch (error) {
      setPermissions({
        owner: resource.owner,
        sharedWith: resource.sharedWith || []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchPermissions();
    }
  }, [visible, resource]);

  const handleShare = async values => {
    setSubmitting(true);
    try {
      const endpoint = type === "folder" ? "folders" : "documents";
      const existingEmails = permissions.sharedWith.map(s => s.user);
      const newEmails = values.userEmails || [];
      const combinedEmails = [...new Set([...existingEmails, ...newEmails])];

      await axios.post(
        `${apiUrl}/${endpoint}/share/${resource._id}`,
        {
          userEmails: combinedEmails,
          permission: values.permission || "read"
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      Success(
        type === "folder" ? t("Folder shared successfully") : t("Document shared successfully")
      );
      fetchPermissions();
      form.resetFields();
      if (updateParent) updateParent();
    } catch (error) {
      Error(t("Failed to share"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePermission = async (email, newPermission) => {
    try {
      const endpoint = type === "folder" ? "folders" : "documents";
      const updatedList = permissions.sharedWith
        .map(s => (s.user === email ? { ...s, permission: newPermission } : s))
        .map(s => s.user);

      await axios.post(
        `${apiUrl}/${endpoint}/share/${resource._id}`,
        { userEmails: updatedList, permission: newPermission },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      fetchPermissions();
      if (updateParent) updateParent();
    } catch (error) {
      Error(t("Failed to update permission"));
    }
  };

  const handleRemoveAccess = async email => {
    try {
      const endpoint = type === "folder" ? "folders" : "documents";
      const updatedList = permissions.sharedWith.filter(s => s.user !== email).map(s => s.user);

      await axios.post(
        `${apiUrl}/${endpoint}/share/${resource._id}`,
        { userEmails: updatedList },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      fetchPermissions();
      if (updateParent) updateParent();
    } catch (error) {
      Error(t("Failed to remove access"));
    }
  };

  return (
    <Modal
      title={`${t("Manage Access")}: ${resource?.name || (type === "folder" ? t("Folder") : t("Document"))}`}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={550}
      className="documents-share-modal"
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleShare}>
          <Row gutter={[8, 0]} align="bottom">
            <Col xs={24} sm={14}>
              <Form.Item name="userEmails" label={t("Add People")}>
                <UserSearchComponent apiUrl={apiUrl} placeholder={t("Email or name...")} />
              </Form.Item>
            </Col>
            <Col xs={14} sm={5}>
              <Form.Item name="permission" label={t("Role")} initialValue="read">
                <Select className="w-100">
                  <Select.Option value="read">{t("Viewer")}</Select.Option>
                  <Select.Option value="write">{t("Editor")}</Select.Option>
                  <Select.Option value="admin">{t("Admin")}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={10} sm={5}>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting} block>
                  {t("Add")}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider className="my-2" />

        <Text strong>{t("Who has access")}</Text>
        <List
          itemLayout="horizontal"
          dataSource={[
            {
              user: permissions.owner?.email || t("Owner"),
              permission: "owner",
              isOwner: true,
              name: permissions.owner
                ? `${permissions.owner.firstName} ${permissions.owner.lastName}`
                : t("Owner")
            },
            ...permissions.sharedWith.map(s => ({ ...s, isOwner: false }))
          ]}
          renderItem={item => (
            <List.Item
              className="documents-share-modal__person"
              actions={
                !item.isOwner
                  ? [
                      <Select
                        key="permission"
                        value={item.permission}
                        onChange={val => handleUpdatePermission(item.user, val)}
                        popupMatchSelectWidth={false}
                        aria-label={t("Role")}
                      >
                        <Select.Option value="read">{t("Viewer")}</Select.Option>
                        <Select.Option value="write">{t("Editor")}</Select.Option>
                        <Select.Option value="admin">{t("Admin")}</Select.Option>
                      </Select>,
                      <Button
                        key="remove"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t("Remove access")}
                        onClick={() => handleRemoveAccess(item.user)}
                      />
                    ]
                  : [
                      <Text key="owner" type="secondary">
                        {t("Owner")}
                      </Text>
                    ]
              }
            >
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={item.name || item.user}
                description={item.user}
              />
            </List.Item>
          )}
        />
      </Spin>
    </Modal>
  );
};

export default ShareModal;
